import { Icon, IconName } from '@repo/ui';
import c from 'classnames';
import MobileMenu from 'components/MobileMenu/MobileMenu';
import { AnimatePresence } from 'framer-motion';
import useGetParsedPartnerNotes from 'hooks/partnerNotes/mobile/useGetMobilePartnerNotesItems';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import useFeature from 'services/features/useFeature';
import useSubscriptions from 'services/general/subscriptionsList';
import { getHomePageTinyAccountId } from 'store/home';
import { getLatestKitForTinyAccountId } from 'store/kits';
import { getKits } from 'store/shared';
import styles from './MobileNav.module.scss';
import getNavItemParameters from './utils/getNavItemParameters';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';

export const MobileNavItem = ({
  pathname,
  iconName,
  activeIconName,
  label,
  tinyAccountId,
  color = 'green',
  iconClassName,
  onClick,
}: {
  pathname: string;
  iconName: IconName;
  activeIconName?: IconName;
  label: string;
  tinyAccountId?: string | null;
  color?: string;
  iconClassName?: string;
  onClick?: VoidFunction;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = useMemo(() => {
    return pathname.length
      ? location.pathname.startsWith(`/${pathname}`)
      : location.pathname === '/';
  }, [location, pathname]);

  return (
    <div
      className={styles.navMenuItem}
      onClick={() => {
        if (onClick) onClick();
        navigate(`/${pathname}`, {
          state: { tinyAccountId, from: location.pathname },
        });
      }}
    >
      <Icon
        name={isActive && activeIconName ? activeIconName : iconName}
        className={c(
          isActive && styles[color],
          iconClassName && styles[iconClassName],
          isActive && styles.activeIcon,
        )}
      />
      <span className={c(styles.navMenuItemLabel, isActive && styles[color])}>
        {label}
      </span>
    </div>
  );
};

const MobileNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const homeScreenMatch = useMatch('/');
  const resultsMatch = useMatch<'kitId', string>('/results/:kitId');
  const actionPlanMatch = useMatch<'kitId', string>('/action-plan/:kitId');
  const homePageTinyAccountId = useSelector(getHomePageTinyAccountId);
  const mostRecentKitIdFromHomePage = useSelector(
    getLatestKitForTinyAccountId(homePageTinyAccountId ?? null),
  )?.id;
  const [mostRecentKitId, setMostRecentKitId] = useState(currentKitId);
  const kitIdParam = homeScreenMatch
    ? (mostRecentKitIdFromHomePage ?? '')
    : resultsMatch?.params.kitId ||
      actionPlanMatch?.params.kitId ||
      currentKitId ||
      mostRecentKitIdFromHomePage ||
      '';
  const allKits = useSelector(getKits);
  const hasPartnerKits = allKits.some(k => !!k.partner_id);
  const { isAnyTinyPlus } = useSubscriptions();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();

  const { trackEvent } = useAnalytics();

  const [partnerNote] = useGetParsedPartnerNotes();

  const { isFeatureEnabled: isMembershipPageEnabled } =
    useFeature('membership_page');
  const { isFeatureEnabled: isPartnerRecommendationNoteEnabled } = useFeature(
    'partner_recommendation_note',
  );

  const showMembershipItem =
    isMembershipPageEnabled &&
    !isAnyTinyPlus &&
    !hasPartnerKits &&
    !portalConfig.hide_upsells &&
    !isPortalConfigLoading;

  useEffect(() => {
    if (kitIdParam && kitIdParam !== mostRecentKitId) {
      setMostRecentKitId(kitIdParam);
    }
  }, [kitIdParam, mostRecentKitId]);

  return (
    <>
      <AnimatePresence>
        {mobileMenuOpen && <MobileMenu toggleMobileMenu={toggleMobileMenu} />}
      </AnimatePresence>
      <div className={styles.mobileNav}>
        <MobileNavItem
          pathname={``}
          iconName='home'
          label='Home'
          tinyAccountId={currentTinyAccountId}
        />
        <MobileNavItem
          pathname={`results/${mostRecentKitId ?? ''}`}
          iconName='lightOn'
          label='Results'
        />
        <MobileNavItem
          pathname={`action-plan/${mostRecentKitId ?? ''}`}
          iconName='today'
          label='Action'
        />

        <MobileNavItem
          {...getNavItemParameters({
            partnerNote,
            showMembershipItem,
            isPartnerRecommendationNoteEnabled,
            trackEvent,
          })}
        />

        <div className={styles.navMenuItem} onClick={toggleMobileMenu}>
          <Icon name='hamburger' active={mobileMenuOpen} />
          <span
            className={c(
              styles.navMenuItemLabel,
              mobileMenuOpen && styles.active,
            )}
          >
            Menu
          </span>
        </div>
      </div>
    </>
  );
};
export default MobileNav;
